// Top bar (CONTRACT "Top bar"): 44 px on the floor step. Mark, workspace
// control, the Sources state chip, the global search with its "/" cap and
// suggestions, the notifications bell, the inspector toggle, and the account
// chip.
//
// The market-data chip (owner spec 2026-08-28: "Market data: Apify,
// CoinGecko, PumpPortal live") is gone from this bar; that state is still
// reachable from Provider health, and stays on the under-1024 menu sheet
// (MobileMenu.tsx), which still has room for it.
//
// Everything here is neutral: the blue policy allows blue only on active,
// selected, focus and live, so the unread count is a raised neutral chip and
// the only accent in this row is a live presence dot. Every chip is a real
// control, because hover is never the only path to what it says.
import { Bell, CaretDown, List, SidebarSimple } from '@phosphor-icons/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postLogout, postWorkspaceSwitch } from '../api/endpoints';
import { useWorkspaces } from '../api/hooks';
import { queryKeys } from '../api/queryKeys';
import type { AuthIdentity, WorkspacesResponse, WorkspaceRole } from '../api/types';
import { useView, type ViewId } from '../router/useView';
import { Button } from '../ui/Button';
import { CountChip } from '../ui/Chip';
import { Menu, type MenuItem } from '../ui/Menu';
import type { Presence } from '../ui/PresenceDot';
import { GlobalSearch } from './GlobalSearch';
import type { LoadedRecordSource } from './searchModel';
import { useShell } from './ShellContext';

export const PRODUCT_NAME = 'Aleph';

export interface StateChip {
  readonly presence: Presence;
  readonly text: string;
  readonly detail: string;
}

export interface TopBarProps {
  readonly identity: AuthIdentity | null;
  readonly accessRole?: WorkspaceRole | null;
  readonly sources: StateChip;
  readonly unreadAlerts: number | null;
  readonly loadedRecords: LoadedRecordSource;
  readonly onSignedOut: () => void;
}

// `linked` is false wherever a navigation would destroy the screen it sits
// on: the auth door carries one-time tokens in the query string (?invite=,
// ?verify_email=, ?reset_password=), and a click on a mark pointing at
// ?view=live stripped them with no way back except the email. A logo is not
// worth a lost invitation, so off the shell the mark is a plain mark.
//
// `wordmark` is false in the top bar, where the workspace control beside it
// prints the workspace's real name: the live workspace is called "Aleph", so
// a wordmark plus that control said "Aleph" twice in the top 44 px. The glyph
// carries the identity there and the accessible name still says Aleph.
export function AlephMark({
  linked = true,
  wordmark = true,
}: {
  readonly linked?: boolean;
  readonly wordmark?: boolean;
}) {
  const inner = (
    <>
      <svg className="mark-glyph" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
        <rect width="24" height="24" rx="6" fill="var(--color-raised)" />
        <path
          d="M6 6l12 12M14 6c1.5 0 2.5 1 2.5 2.5V11M10 18c-1.5 0-2.5-1-2.5-2.5V13"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
      {wordmark ? <span className="mark-tx">{PRODUCT_NAME}</span> : null}
    </>
  );
  if (!linked) return <span className="mark">{inner}</span>;
  return (
    <a className="mark" href="?view=live" aria-label={`${PRODUCT_NAME} home`}>
      {inner}
    </a>
  );
}

// The control prints the workspace's real name, always. It used to fall back
// to the literal word "Workspace" whenever the tenant name matched the product
// name, which put a placeholder-shaped noun in the top 44 px of every screen.
export function workspaceLabel(identity: AuthIdentity | null): string {
  const name = identity?.tenant.name.trim() ?? '';
  return name === '' ? 'Workspace' : name;
}

// shell.html labels this control "main workspace": the name, then the word
// that says what the name is. The word rides as a muted second span so the
// name keeps the weight, and it is dropped when the name already carries it.
export function workspaceQualified(identity: AuthIdentity | null): boolean {
  const name = workspaceLabel(identity).toLowerCase();
  return name !== 'workspace' && !name.endsWith(' workspace');
}

function isWorkspaceScopedQuery(queryKey: readonly unknown[]): boolean {
  return queryKey[0] !== 'auth' && queryKey[0] !== 'health';
}

function WorkspaceControl({ identity }: { readonly identity: AuthIdentity | null }) {
  const { navigate } = useView();
  const queryClient = useQueryClient();
  const workspaces = useWorkspaces();
  const { toast } = useShell();
  const switchTo = useMutation({
    mutationFn: postWorkspaceSwitch,
    onSuccess: async (next) => {
      // The server session has already moved to the new tenant. Cancel and
      // evict every old tenant projection before changing the workspace label,
      // so a failed refetch can never retain decision data under the new name.
      const workspaceData = {
        predicate: (query: { readonly queryKey: readonly unknown[] }) =>
          isWorkspaceScopedQuery(query.queryKey),
      };
      await queryClient.cancelQueries(workspaceData);
      queryClient.removeQueries(workspaceData);
      queryClient.setQueryData(queryKeys.authMe(), next);
      queryClient.setQueryData<WorkspacesResponse>(queryKeys.workspaces(), (current) =>
        current ? { ...current, activeTenantId: next.tenant.id } : current,
      );
      void queryClient.invalidateQueries();
      toast.show(`Opened ${next.tenant.name}`);
    },
    onError: (error) =>
      toast.show(
        error instanceof Error
          ? `Workspace switch failed: ${error.message}`
          : 'Workspace switch failed.',
      ),
  });

  const rows = workspaces.data?.workspaces ?? [];
  const activeId = workspaces.data?.activeTenantId ?? identity?.tenant.id ?? null;
  const items: MenuItem[] = rows.map((workspace) => ({
    id: workspace.id,
    label:
      workspace.id === activeId
        ? `${workspace.name}, ${workspace.role}, current`
        : `${workspace.name}, ${workspace.role}`,
    disabled: workspace.id === activeId || switchTo.isPending,
    onSelect: () => switchTo.mutate(workspace.id),
  }));
  if (rows.length === 0) {
    items.push({
      id: 'workspaces-unavailable',
      label: workspaces.isPending ? 'Reading your workspaces' : 'Workspace list unavailable',
      disabled: true,
      onSelect: () => undefined,
    });
  }
  items.push({
    id: 'members',
    label: 'Members and invitations',
    onSelect: () => navigate('settings'),
  });

  return (
    <Menu
      align="start"
      label={identity ? `Workspace ${identity.tenant.name}, role ${identity.role}` : 'Workspace'}
      triggerClassName="ws"
      trigger={
        <>
          <span className="ws-tx">{workspaceLabel(identity)}</span>
          {workspaceQualified(identity) ? <span className="ws-kind">workspace</span> : null}
          <CaretDown className="ws-caret" aria-hidden="true" />
        </>
      }
      items={items}
    />
  );
}

// The chip is the CountChip primitive inside a button, not a copy of its
// classes: a bare `.chip-count` here only painted on destinations whose view
// happened to import Chip.css, so the persistent chrome lost its chips on
// Settings (redo-1 P1). Composing the primitive ships its stylesheet with the
// shell and keeps the two from drifting.
export function StateChipButton({
  chip,
  navigateTo,
  onSelect,
}: {
  readonly chip: StateChip;
  readonly navigateTo: ViewId;
  readonly onSelect?: (() => void) | undefined;
}) {
  const { navigate } = useView();
  return (
    <button
      type="button"
      className="chip-btn"
      aria-label={`${chip.text}. ${chip.detail}`}
      title={chip.detail}
      onClick={() => {
        navigate(navigateTo);
        onSelect?.();
      }}
    >
      <CountChip presence={chip.presence} quiet>
        {chip.text}
      </CountChip>
    </button>
  );
}

export function TopBar({
  identity,
  accessRole,
  sources,
  unreadAlerts,
  loadedRecords,
  onSignedOut,
}: TopBarProps) {
  const { navigate } = useView();
  const { inspectorOpen, toggleInspector, listOpen, toggleList, menuOpen, setMenuOpen, toast } =
    useShell();
  const queryClient = useQueryClient();

  const logout = useMutation({
    mutationFn: postLogout,
    onSuccess: () => {
      queryClient.clear();
      onSignedOut();
    },
    onError: (error) =>
      toast.show(error instanceof Error ? `Sign out failed: ${error.message}` : 'Sign out failed.'),
  });

  const owner = identity?.user.displayName ?? 'account';
  const initial = owner.trim().charAt(0).toLowerCase() || 'a';

  return (
    <header className="topbar" role="banner" data-region="topbar">
      {/* Three zones, not a flat row. The first fix made the centre track
          absorb whatever the left and right zones (both sized to their own
          content) left over, which centred the search inside THAT leftover
          space rather than on the page: against a wider left cluster (menu,
          mark, workspace, one chip) the narrower right one (bell, list,
          inspector, account) left more room on the right, so the leftover
          track's own middle sat right of the bar's true centre (owner spec
          2026-08-28: "you didnt do anything about the searchbar"). The left
          and right zones now share the identical flexible track (minmax(0,
          1fr) on both, AppShell.css), so whichever needs more room, both get
          that much and stay equal, and the centre track (auto, sized to the
          search itself) lands at the bar's real midpoint. */}
      <div className="topbar-left">
        <Button
          className="menubtn"
          variant="icon"
          aria-label="Open destinations menu"
          aria-expanded={menuOpen}
          icon={<List />}
          onClick={() => setMenuOpen(!menuOpen)}
        />
        <AlephMark wordmark={false} />
        <WorkspaceControl identity={identity} />
        <StateChipButton chip={sources} navigateTo="sources" />
      </div>
      <div className="topbar-center">
        <GlobalSearch loaded={loadedRecords} role={accessRole ?? identity?.role ?? null} />
      </div>
      <div className="topbar-right">
        <span className="bellwrap">
          <Button
            variant="icon"
            aria-label={unreadAlerts ? `Notifications, ${unreadAlerts} unread` : 'Notifications'}
            icon={<Bell />}
            onClick={() => navigate('alerts')}
          />
          {unreadAlerts ? (
            <span className="bell-n num" aria-hidden="true">
              {unreadAlerts > 99 ? '99+' : unreadAlerts}
            </span>
          ) : null}
        </span>
        {/* The list's own collapse control lives inside the list, so collapsing
            it by pointer used to leave no way back: the button that reopens it
            had gone with the column. This is the mirror of the inspector
            toggle, and the two sit together. */}
        <Button
          className="listbtn"
          variant="icon"
          aria-label="Toggle context list, key ["
          aria-pressed={listOpen}
          icon={<SidebarSimple />}
          onClick={toggleList}
        />
        <Button
          variant="icon"
          aria-label="Toggle inspector, key ]"
          aria-pressed={inspectorOpen}
          icon={<SidebarSimple mirrored />}
          onClick={toggleInspector}
        />
        <Menu
          label={`Account menu, ${owner}`}
          trigger={
            <span className="account">
              <span className="avatar">{initial}</span>
              <span className="account-nm">{owner}</span>
              <CaretDown className="ws-caret" aria-hidden="true" />
            </span>
          }
          triggerClassName="account-btn"
          items={[
            { id: 'settings', label: 'Settings', onSelect: () => navigate('settings') },
            {
              id: 'signout',
              label: logout.isPending ? 'Signing out' : 'Sign out',
              onSelect: () => logout.mutate(),
              disabled: logout.isPending,
            },
          ]}
        />
      </div>
    </header>
  );
}
